import { NotFoundError } from "domain/Error";
import { Magnolia } from "domain/Magnolia";
import { HTTPClientService } from "services/HTTPClient";
import {
  getAPIBase,
  getCurrentLanguage,
  getCurrentMultisiteBase,
} from "util/MagnoliaAppHelpers";
import {
  getMultisiteLanguages,
  isSingleTreeMultisite,
} from "util/multisiteLanguages";

const basePath = getAPIBase();
const restJCRBase = basePath + process.env.REACT_APP_MGNL_API_PAGES;
const restJCRMetaBase = basePath + process.env.REACT_APP_MGNL_API_PAGES_META;
const restContactBase = basePath + process.env.REACT_APP_MGNL_API_CONTACT;
const restTemplatesBase = basePath + process.env.REACT_APP_MGNL_API_TEMPLATES;
const restNavigationBase = basePath + process.env.REACT_APP_MGNL_API_NAV;

// avoids unneccessary calls to magnolia that will fail anyway because of app structure of components
// see App.tsx for more
const exeptionPaths = [
  RegExp(/product-search\/\w+/),
  RegExp(/products-overview\/\w+/),
  RegExp(/products-brands\/\w+/),
  RegExp(/info-center\/press\/\w+/),
  RegExp(/info-center\/blog\/\w+/),
  // Backward compatibility: old format
  RegExp(/press~\/\w+/),
  RegExp(/blog~\/\w+/),
  RegExp(/htf-products\/\w+/),
  RegExp(/career\/graduate\/\w+/),
  RegExp(/career\/student\/\w+/),
  RegExp(/career\/professional\/\w+/),
  RegExp(/career\/job-starter\/\w+/),
  RegExp(/orders\/\w+/),
  RegExp(/playground\/\w+/),
  RegExp(/industries\/\w+/),
  RegExp(/career\/jobs\/\w+/),
  RegExp(/career\/job\/\w+/),
  RegExp(/career\/job-cf\/\w+/),
  RegExp(/about\/career\/job\/\w+/),
  RegExp(/job\/\w+/),
];

declare global {
  interface Window {
    CACHE: {
      pageData: {
        [pagePath: string]: Magnolia.PageMetaData | undefined;
      };
      pageMetaData: {
        [pagePath: string]: Magnolia.PageMetaData | false | undefined;
      };
      tplData: {
        [templateId: string]: { [key: string]: Magnolia.Template } | undefined;
      };
      contactData: {
        [path: string]: Magnolia.Contact | undefined;
      };
      navData: {
        [languageKey: string]: Magnolia.NavigationData | undefined;
      };
      topLevelArea: { [area: string]: Magnolia.Area };
      /**
       * Set to `true` when the current page render is the result of a 404
       * fallback (the requested page was missing in JCR and the configured
       * `<lang>/404` page is being rendered in its place). Used by
       * `BasicHeadTags` to emit the `prerender-status-code: 404` meta tag so
       * the response is treated as a real 404 by crawlers and the
       * prerenderer, even though the browser URL is the originally-requested
       * path.
       */
      servedAsNotFound?: boolean;
    };
  }
}

window.CACHE = {
  pageData: {},
  pageMetaData: {},
  tplData: {},
  topLevelArea: {},
  contactData: {},
  navData: {},
  servedAsNotFound: false,
};

export default class ContentService {
  readonly httpClientService: HTTPClientService;

  private redirectPageCache: {
    [pagePath: string]: Magnolia.PageMetaData | null;
  } = {};

  constructor(httpClientService: HTTPClientService) {
    this.httpClientService = httpClientService;
  }

  /**
   * Removes the locale segment from a URL path for i18n migration.
   * Example: "albis-distribution/de/about" -> "albis-distribution/about"
   */
  private removeLocaleFromUrl(url: string): string {
    const multisiteBase = getCurrentMultisiteBase();
    const languages = getMultisiteLanguages(multisiteBase);
    
    // Remove any language code from the URL path
    for (const lang of languages) {
      // Pattern: /multisiteBase/locale/ or /multisiteBase/locale (at end)
      url = url
        .replace(`/${multisiteBase}/${lang}/`, `/${multisiteBase}/`)
        .replace(`/${multisiteBase}/${lang}`, `/${multisiteBase}`);
    }
    
    return url;
  }

  /**
   * Helper method for API calls with i18n support.
   * For albis-distribution: uses DefaultI18nContentSupport format with ?lang= query parameter.
   * For other sites: uses the URL as provided (with locale in path).
   */
  private async backwardCompatibleGet<T>(urlWithLocale: string): Promise<T> {
    const httpClient = this.httpClientService.getAlbisHttpClient();
    const isAlbisDistribution = getCurrentMultisiteBase() === "albis-distribution";

    // For albis-distribution: use query parameter format only
    if (isAlbisDistribution) {
      const currentLang = getCurrentLanguage();
      const urlWithoutLocale = this.removeLocaleFromUrl(urlWithLocale);
      const newUrl = `${urlWithoutLocale}?lang=${currentLang}`;
      const { data } = await httpClient.get<T>(newUrl);
      return data;
    }

    // For sites other than albis-distribution, use the URL as-is (with locale in path)
    const { data } = await httpClient.get<T>(urlWithLocale);
    return data;
  }

  normalizePagePath(pagePath: string) {
    const multisiteBase = getCurrentMultisiteBase();
    const languages = getMultisiteLanguages(multisiteBase);
    // Single-tree multisites (e.g. albis-distribution) use
    // SingleTreeI18nContentSupport: the locale is NOT a JCR node, it's served
    // via `?lang=`, so the locale segment must be stripped from the path.
    // Multi-tree multisites (e.g. mocom, krahn-ceramics) use
    // HierarchyBasedI18nContentSupport: the locale IS a real node
    // (`mocom/de/...`), so it must be preserved or the fetch resolves to the
    // bare site root and the backend redirects to the default locale.
    const singleTree = isSingleTreeMultisite(multisiteBase);

    // Split into segments, drop empties (handles leading/trailing/duplicate slashes).
    const segments = pagePath.split("/").filter(Boolean);

    // Strip the leading site segment always. For single-tree multisites also
    // strip leading language segments (in any order). Handles all of these
    // inputs and produces the same canonical output:
    //   /albis-distribution/markets/overview
    //   /en/albis-distribution/markets/overview        (single-tree only)
    //   /albis-distribution/en/markets/overview        (single-tree only)
    //   /en/markets/overview                            (single-tree only)
    while (
      segments.length > 0 &&
      (segments[0] === multisiteBase ||
        (singleTree && languages.includes(segments[0])))
    ) {
      segments.shift();
    }

    return [multisiteBase, ...segments].join("/");
  }

  // eslint-disable-next-line
  getTopLevelArea = async <T extends any>(
    area: string,
    force = false
  ): Promise<Magnolia.Area & T> => {
    if (window.CACHE.topLevelArea[area] && !force) {
      return Promise.resolve(
        window.CACHE.topLevelArea[area] as Magnolia.Area & T
      );
    }
    const url = `${restJCRBase}/${getCurrentMultisiteBase()}/${getCurrentLanguage()}/${area}`;
    const data = await this.backwardCompatibleGet<Magnolia.Area & T>(url);
    window.CACHE.topLevelArea[area] = data;
    return data;
  };

  gatherAllPageData = async (pagePath: string, force = false) => {
    return await Promise.all([
      this.loadPage(pagePath, force),
      this.loadNavigationData(force),
    ]);
  };

  private loadPage = async (
    pagePath: string,
    force = false
  ): Promise<Magnolia.PageMetaData> => {
    if (window.CACHE.pageData[pagePath] && !force) {
      // A cached hit means we know this page exists; clear any stale
      // "served as 404" flag from a previous navigation.
      window.CACHE.servedAsNotFound = false;
      return Promise.resolve(window.CACHE.pageData[pagePath]!);
    }

    const normalized = this.normalizePagePath(pagePath);
    const url = `${restJCRBase}/${normalized}`;
    try {
      const data = await this.backwardCompatibleGet<Magnolia.PageMetaData>(url);
      window.CACHE.pageData[pagePath] = data;
      window.CACHE.servedAsNotFound = false;
      return data;
    } catch (e) {
      if (!(e instanceof NotFoundError)) throw e;

      // The page doesn't exist in JCR. Fetch the configured 404 page from the
      // same REST endpoint and render it in place of the requested page.
      // The browser URL stays the originally-requested URL (correct UX for a
      // 404) and `BasicHeadTags` emits a `prerender-status-code: 404` meta.
      const notFoundPath = `/${getCurrentLanguage()}/404/`;
      const normalizedNotFound = this.normalizePagePath(notFoundPath);

      // Guard: if the request that 404'd already *was* the 404 page itself,
      // don't recurse. Let the original NotFoundError bubble so the caller
      // can render its last-resort error UI.
      if (normalized === normalizedNotFound) throw e;

      const fallbackUrl = `${restJCRBase}/${normalizedNotFound}`;
      const data = await this.backwardCompatibleGet<Magnolia.PageMetaData>(
        fallbackUrl
      );
      // Cache under the original path so repeated renders/route changes back
      // to this missing URL don't re-fetch.
      window.CACHE.pageData[pagePath] = data;
      window.CACHE.servedAsNotFound = true;
      return data;
    }
  };

  /**
   * Checks whether an editor-maintained redirect page (template
   * `albis:pages/redirect`) exists at the given URL path.
   *
   * Needed for legacy URLs below app-routed pages (e.g. retired products
   * under products-overview): the routingExceptions in App.tsx truncate such
   * paths before the PageLoader fetches them, so redirect pages created in
   * the CMS underneath are never resolved through normal page loading.
   * Unlike `loadPage` this never falls back to the 404 page, and unlike
   * `loadPageMetaData` it deliberately bypasses the `exeptionPaths` guard.
   */
  findRedirectPage = async (
    pagePath: string
  ): Promise<Magnolia.PageMetaData | null> => {
    if (this.redirectPageCache[pagePath] !== undefined) {
      return this.redirectPageCache[pagePath];
    }
    let redirectPage: Magnolia.PageMetaData | null = null;
    try {
      const data = await this.backwardCompatibleGet<Magnolia.PageMetaData>(
        `${restJCRBase}/${this.normalizePagePath(pagePath)}`
      );
      // template id mapped to the RedirectPage component in magnolia.config.ts
      if (data["mgnl:template"] === "albis:pages/redirect") {
        redirectPage = data;
      }
    } catch (e) {
      if (!(e instanceof NotFoundError)) throw e;
    }
    this.redirectPageCache[pagePath] = redirectPage;
    return redirectPage;
  };

  loadPageMetaData = async (
    pagePath: string,
    force = false
  ): Promise<Magnolia.PageMetaData | false> => {
    if (window.CACHE.pageData[pagePath] !== undefined && !force) {
      return Promise.resolve(window.CACHE.pageMetaData[pagePath]!);
    }
    // avoids unneccessary calls to magnolia that will fail anyway because of app structure of components
    if (
      exeptionPaths.some((path) => path.test(pagePath)) &&
      pagePath !== "/industries/scientific-instruments" &&
      pagePath !== "/industries/krahn-specialty-fluids"
    ) {
      return false;
    }

    const url = `${restJCRMetaBase}/${this.normalizePagePath(pagePath)}`;
    try {
      const data = await this.backwardCompatibleGet<Magnolia.PageMetaData>(url);
      window.CACHE.pageMetaData[pagePath] = data;
      return data;
    } catch (e) {
      window.CACHE.pageMetaData[pagePath] = false;
      return false;
    }
  };

  loadTemplateDefinitions = async (
    templateId: string,
    force = false
  ): Promise<{ [key: string]: Magnolia.Template }> => {
    if (window.CACHE.tplData[templateId] && !force) {
      return Promise.resolve(window.CACHE.tplData[templateId]!);
    }
    const httpClient = this.httpClientService.getAlbisHttpClient();
    const { data } = await httpClient.get<{ [key: string]: Magnolia.Template }>(
      restTemplatesBase + "/" + templateId
    );
    window.CACHE.tplData[templateId] = data;
    return data;
  };

  getNavigationData = () => {
    const multisiteBase = getCurrentMultisiteBase();
    const isAlbisDistribution = multisiteBase === "albis-distribution";
    
    // For single-tree multisites, cache is per-language
    // For multi-tree multisites, cache is per-multisite (contains all languages)
    const cacheKey = isAlbisDistribution 
      ? `${multisiteBase}-${getCurrentLanguage()}`
      : multisiteBase;
    
    return window.CACHE.navData[cacheKey];
  };
  
  private loadNavigationData = async (
    force = false
  ): Promise<Magnolia.NavigationData> => {
    const multisiteBase = getCurrentMultisiteBase();
    const isAlbisDistribution = multisiteBase === "albis-distribution";
    const httpClient = this.httpClientService.getAlbisHttpClient();
    
    // For single-tree multisites, cache is per-language
    // For multi-tree multisites, cache is per-multisite (contains all languages)
    const cacheKey = isAlbisDistribution 
      ? `${multisiteBase}-${getCurrentLanguage()}`
      : multisiteBase;
    
    if (window.CACHE.navData[cacheKey] && !force) {
      return Promise.resolve(window.CACHE.navData[cacheKey]!);
    }
    
    // For single-tree (albis-distribution): use query parameter format
    // For multi-tree (mocom, etc.): language is in the tree structure
    let url: string;
    if (isAlbisDistribution) {
      url = `${restNavigationBase}/${multisiteBase}?lang=${getCurrentLanguage()}`;
    } else {
      url = `${restNavigationBase}/${multisiteBase}`;
    }
    
    const { data } = await httpClient.get<Magnolia.NavigationData>(url);
    window.CACHE.navData[cacheKey] = data;
    return data;
  };

  loadContact = async (
    path: string,
    force = false
  ): Promise<Magnolia.Contact> => {
    if (window.CACHE.contactData[path] && !force) {
      return Promise.resolve(window.CACHE.contactData[path]!);
    }
    const httpClient = this.httpClientService.getAlbisHttpClient();
    const { data } = await httpClient.get<Magnolia.Contact>(
      `${restContactBase}${path}`
    );
    window.CACHE.contactData[path] = data;
    return data;
  };
}
